import React, { useEffect, useState } from 'react';
import {
	ILabel,
	IListItemProps,
	IOrderInfoDetails,
	ITollSectionOffer,
	ITypeVignetteOrderingProcessFields,
	formatTripsAmountLabel,
	tollSectionIsDisabled,
} from '@adac-mp/shared';
import {
	Typography,
	ToggleGroup,
	RadioButtons,
	renderTripsAmount,
	HintMessage,
	List,
	getHintContent,
	AdvancedOptionType,
} from '../../index';

interface ITollSectionConfigurationProps {
	simple?: boolean;
	route?: ITollSectionOffer | IOrderInfoDetails;
	hasError: boolean;
	productCode: string;
	initSubroute?: string;
	correspondingTollSections?: ITollSectionOffer[];
	labels: ILabel[];
	contentfulLabels: ITypeVignetteOrderingProcessFields;
	setHasError: (hasError: boolean) => void;
	setProductCode: (productCode: string) => void;
}

export const TollSectionConfiguration = ({
	simple = false,
	hasError,
	productCode,
	route,
	correspondingTollSections,
	initSubroute,
	labels,
	contentfulLabels,
	setHasError,
	setProductCode,
}: ITollSectionConfigurationProps) => {
	const currentLabels = contentfulLabels;
	const tollSectionInfo = currentLabels?.tollSectionInfo;
	const [routeInfo, setRouteInfo] = useState<string | undefined>();
	const [tollSectionOptions, setTollSectionOptions] = useState<ITollSectionOffer[] | undefined>();

	//case for A9 Pyhrn route with subsections
	const [subroutes, setSubroutes] = useState<AdvancedOptionType[] | undefined>();
	const [defaultSubroute, setDefaultSubroute] = useState('');
	const [subroute, setSubroute] = useState('');

	useEffect(() => {
		const currSubroutes = correspondingTollSections
			?.filter(({ sectionName, trips }) => sectionName && trips === 1)
			?.map(({ sectionName, downtimes }) => ({
				id: sectionName,
				label: sectionName,
				disabled: tollSectionIsDisabled(downtimes),
			})) as AdvancedOptionType[];
		if (currSubroutes.length > 0) {
			setSubroutes(currSubroutes);
			const initDefaultSubroute = currSubroutes.reduce((acc, curr) =>
				acc.id.length > curr.id.length ? acc : curr,
			);
			setDefaultSubroute(initDefaultSubroute.id);
			setSubroute(initSubroute ?? initDefaultSubroute.id);
		}
		return () => {
			if (!productCode) {
				setHasError(false);
				setSubroute(defaultSubroute);
			}
		};
	}, []);

	useEffect(() => {
		if (subroute) {
			const selectedSubroute = correspondingTollSections?.filter(({ sectionName }) => sectionName === subroute);
			if (selectedSubroute) {
				setTollSectionOptions(selectedSubroute);
				setRouteInfo(selectedSubroute[0].routeInfo);
			}
		} else {
			setTollSectionOptions(correspondingTollSections);
			if (correspondingTollSections) setRouteInfo(correspondingTollSections[0].routeInfo);
		}
	}, [subroute]);

	const displayInformation: IListItemProps[] = [
		{
			label: tollSectionInfo?.section,
			value: routeInfo,
			isHighlighted: true,
		},
		{
			label: tollSectionInfo?.validityPeriod,
			value: tollSectionInfo?.amountValidityPeriod,
			isHighlighted: true,
		},
	];

	return (
		<>
			{!simple &&
				route?.downtimes &&
				route.downtimes.length > 0 &&
				route.downtimes[0]?.type === 'warn' &&
				route.downtimes.map(({ id, message }) => (
					<HintMessage
						key={id}
						padding="small"
						className="mb-4"
						hints={[{ tip: tollSectionInfo?.hint, message }]}
					/>
				))}
			{!simple && route?.routeDirection && (
				<HintMessage
					className={`mb-3.5`}
					padding="small"
					hints={getHintContent('tollSectionSingleTrip', [], currentLabels?.cart?.hints)}
				/>
			)}
			{!simple && (
				<List
					columnRow
					noBorder
					wrapperClassName="mb-4"
					labelVariant="title"
					items={displayInformation.filter(({ value }) => value)}
				/>
			)}
			{subroutes && (
				<>
					{!simple && (
						<Typography component="p" className="pl-3 mb-1.5" size="lgEven" variant="title" color="title">
							{tollSectionInfo?.chooseRoute}
						</Typography>
					)}
					<ToggleGroup
						noUnselect
						wrapperClassName="px-3 grid grid-cols-2 grid-rows-2 gap-1.5 mb-4 md:grid-rows-1 md:grid-cols-3"
						className="border px-2 rounded border-gray-500 py-[7px] order-2 last:col-span-2 last:order-1 checked:border-2 checked:border-primary md:last:col-span-1 md:py-2"
						defaultValue={defaultSubroute}
						options={subroutes}
						value={subroute}
						setValue={(val) => {
							setProductCode('');
							setSubroute(val);
							setHasError(false);
						}}
					/>
				</>
			)}
			{tollSectionOptions && (
				<>
					{!simple && (
						<Typography component="p" className="pl-3 mb-1.5" size="lgEven" variant="title" color="title">
							{tollSectionInfo?.chooseTrip}
						</Typography>
					)}
					<RadioButtons
						hasError={hasError}
						helperText={tollSectionInfo?.errorTrips || 'Bitte wählen Sie die Anzahl der Fahrten aus.'}
						helperTextStyle={{
							marginTop: '10px',
							gap: '12px',
							paddingInline: '16px',
							paddingBottom: '8px',
						}}
						className="[&:not(:last-of-type)]:border-b [&:not(:last-of-type)]:border-solid border-l-0 border-t-0 border-r-0 border-b-gray-600 py-2 md:py-3"
						labelClassName="w-full"
						wrapperClassName="px-3"
						value={productCode}
						options={tollSectionOptions.map(
							({ serviceCatalogCode, serviceCatalogName, trips, prices, downtimes }) => {
								const routeIsDisabled = tollSectionIsDisabled(downtimes);
								return {
									id: serviceCatalogCode,
									label: renderTripsAmount(prices, trips, labels, routeIsDisabled),
									title: `${serviceCatalogName}${formatTripsAmountLabel(trips, labels)}`,
									disabled: routeIsDisabled,
								};
							},
						)}
						setValue={(val) => {
							setProductCode(val);
							setHasError(false);
						}}
					/>
				</>
			)}
		</>
	);
};
